/**
 * Network Service for managing multi-chain operations
 * Handles network switching, validation, and network-related operations
 */

import {
  NetworkConfig,
  getNetworkConfig,
  isSupportedNetwork,
  getNetworkDisplayName,
  BSC_NETWORK_ID,
  DEFAULT_NETWORK_ID
} from '../config/networks';
import {
  getTokensForNetwork,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getTokenAddress,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isNativeToken
} from '../config/tokens';
import { errorLogger, ErrorCategory, ErrorSeverity } from '../utils/errorLogger';

export interface NetworkSwitchResult {
  success: boolean;
  chainId?: number;
  error?: {
    code: number;
    message: string;
    data?: any;
  };
}

export interface AddNetworkResult {
  success: boolean;
  error?: {
    code: number;
    message: string;
    data?: any;
  };
}

export interface NetworkValidationResult {
  isValid: boolean;
  networkConfig?: NetworkConfig;
  error?: string;
}

/**
 * Enhanced ethereum provider interface (extending the existing one)
 */
interface EnhancedEthereumProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  isConnected?: () => boolean;
  on?: (event: string, handler: (data: any) => void) => void;
  removeListener?: (event: string, handler: (data: any) => void) => void;
}

class NetworkService {
  private currentChainId: number | null = null;
  private networkChangeListeners: Set<(chainId: number) => void> = new Set();

  /**
   * Initialize the network service
   */
  async initialize(): Promise<void> {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    try {
      // Get current chain ID
      const chainId = await this.getCurrentChainId();
      this.currentChainId = chainId;

      // Set up event listeners for network changes
      this.setupNetworkChangeListeners();
    } catch (error) {
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.MEDIUM,
        'Failed to initialize network service',
        'Network initialization failed',
        { error },
        error as Error,
        true
      );
    }
  }

  /**
   * Get current chain ID from the provider
   */
  async getCurrentChainId(): Promise<number> {
    if (typeof window === 'undefined' || !window.ethereum) {
      throw new Error('No ethereum provider available');
    }

    try {
      const chainId = await (window.ethereum as EnhancedEthereumProvider).request({ method: 'eth_chainId' });
      return parseInt(chainId, 16);
    } catch (error) {
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.LOW,
        'Failed to get current chain ID',
        'Unable to detect current network',
        { error },
        error as Error,
        true
      );
      throw error;
    }
  }

  /**
   * Switch to a specific network
   */
  async switchToNetwork(chainId: number): Promise<NetworkSwitchResult> {
    if (typeof window === 'undefined' || !window.ethereum) {
      return {
        success: false,
        error: {
          code: -1,
          message: 'No ethereum provider available'
        }
      };
    }

    try {
      // Validate network is supported
      if (!isSupportedNetwork(chainId)) {
        return {
          success: false,
          error: {
            code: 4902,
            message: `Unsupported network: ${chainId}`
          }
        };
      }

      // Try to switch network
      await (window.ethereum as EnhancedEthereumProvider).request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });

      this.currentChainId = chainId;
      this.notifyNetworkChange(chainId);

      return {
        success: true,
        chainId
      };

    } catch (error: any) {
      // If network is not added to wallet, try to add it
      if (error.code === 4902) {
        return await this.addAndSwitchToNetwork(chainId);
      }

      errorLogger.logNetworkError(
        chainId,
        error,
        `Failed to switch to ${getNetworkDisplayName(chainId)}`,
        true
      );

      return {
        success: false,
        error: {
          code: error.code || -1,
          message: error.message || 'Network switch failed',
          data: error.data
        }
      };
    }
  }

  /**
   * Add a network to the wallet and switch to it
   */
  async addAndSwitchToNetwork(chainId: number): Promise<NetworkSwitchResult> {
    if (typeof window === 'undefined' || !window.ethereum) {
      return {
        success: false,
        error: {
          code: -1,
          message: 'No ethereum provider available'
        }
      };
    }

    const networkConfig = getNetworkConfig(chainId);
    if (!networkConfig) {
      return {
        success: false,
        error: {
          code: 4902,
          message: `Unsupported network: ${chainId}`
        }
      };
    }

    try {
      // Try to add the network
      await (window.ethereum as EnhancedEthereumProvider).request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: networkConfig.chainIdHex,
          chainName: networkConfig.displayName,
          nativeCurrency: networkConfig.nativeCurrency,
          rpcUrls: networkConfig.rpcUrls,
          blockExplorerUrls: networkConfig.blockExplorerUrls,
        }],
      });

      this.currentChainId = chainId;
      this.notifyNetworkChange(chainId);

      return {
        success: true,
        chainId
      };

    } catch (error: any) {
      errorLogger.logNetworkError(
        chainId,
        error,
        `Failed to add ${getNetworkDisplayName(chainId)} to wallet`,
        false
      );

      return {
        success: false,
        error: {
          code: error.code || -1,
          message: error.message || 'Failed to add network',
          data: error.data
        }
      };
    }
  }

  /**
   * Validate current network and prompt switch if needed
   */
  async validateAndSwitchNetwork(requiredChainId?: number): Promise<NetworkValidationResult> {
    try {
      const currentChainId = await this.getCurrentChainId();
      const targetChainId = requiredChainId || DEFAULT_NETWORK_ID;

      if (currentChainId === targetChainId) {
        return {
          isValid: true,
          networkConfig: getNetworkConfig(currentChainId)
        };
      }

      // If we need a specific network, try to switch
      if (requiredChainId) {
        const switchResult = await this.switchToNetwork(requiredChainId);
        
        if (switchResult.success) {
          return {
            isValid: true,
            networkConfig: getNetworkConfig(requiredChainId)
          };
        } else {
          return {
            isValid: false,
            error: switchResult.error?.message || 'Network switch failed'
          };
        }
      }

      // Current network is different but no specific network required
      return {
        isValid: true,
        networkConfig: getNetworkConfig(currentChainId)
      };

    } catch (error) {
      return {
        isValid: false,
        error: 'Failed to validate network'
      };
    }
  }

  /**
   * Get network information for current connection
   */
  async getNetworkInfo(): Promise<{
    chainId: number;
    networkConfig: NetworkConfig | undefined;
    isSupported: boolean;
    supportedTokens: any[];
  }> {
    try {
      const chainId = await this.getCurrentChainId();
      const networkConfig = getNetworkConfig(chainId);
      const isSupported = isSupportedNetwork(chainId);
      const supportedTokens = getTokensForNetwork(chainId);

      return {
        chainId,
        networkConfig,
        isSupported,
        supportedTokens
      };
    } catch (error) {
      throw new Error('Failed to get network information');
    }
  }

  /**
   * Switch to BSC network specifically
   */
  async switchToBSC(): Promise<NetworkSwitchResult> {
    return await this.switchToNetwork(BSC_NETWORK_ID);
  }

  /**
   * Switch to Ethereum network specifically
   */
  async switchToEthereum(): Promise<NetworkSwitchResult> {
    return await this.switchToNetwork(DEFAULT_NETWORK_ID);
  }

  /**
   * Check if current network supports a specific token
   */
  async isTokenSupportedOnCurrentNetwork(tokenSymbol: string): Promise<boolean> {
    try {
      const chainId = await this.getCurrentChainId();
      const tokenAddress = getTokenAddress(tokenSymbol, chainId);
      return tokenAddress !== null || isNativeToken(tokenSymbol, chainId);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get token contract address for current network
   */
  async getTokenAddressForCurrentNetwork(tokenSymbol: string): Promise<string | null> {
    try {
      const chainId = await this.getCurrentChainId();
      return getTokenAddress(tokenSymbol, chainId);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get gas price for current network
   */
  async getGasPrice(): Promise<string> {
    if (typeof window === 'undefined' || !window.ethereum) {
      throw new Error('No ethereum provider available');
    }

    try {
      const gasPrice = await (window.ethereum as EnhancedEthereumProvider).request({ method: 'eth_gasPrice' });
      return gasPrice;
    } catch (error) {
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.LOW,
        'Failed to get gas price',
        'Unable to fetch gas price',
        { error },
        error as Error,
        true
      );
      throw error;
    }
  }

  /**
   * Estimate gas for a transaction
   */
  async estimateGas(transactionParams: any): Promise<string> {
    if (typeof window === 'undefined' || !window.ethereum) {
      throw new Error('No ethereum provider available');
    }

    try {
      const gasEstimate = await (window.ethereum as EnhancedEthereumProvider).request({
        method: 'eth_estimateGas',
        params: [transactionParams]
      });
      return gasEstimate;
    } catch (error) {
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.MEDIUM,
        'Failed to estimate gas',
        'Gas estimation failed',
        { transactionParams, error },
        error as Error,
        true
      );
      throw error;
    }
  }

  /**
   * Get block number for current network
   */
  async getBlockNumber(): Promise<number> {
    if (typeof window === 'undefined' || !window.ethereum) {
      throw new Error('No ethereum provider available');
    }

    try {
      const blockNumber = await (window.ethereum as EnhancedEthereumProvider).request({ method: 'eth_blockNumber' });
      return parseInt(blockNumber, 16);
    } catch (error) {
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.LOW,
        'Failed to get block number',
        'Unable to fetch block number',
        { error },
        error as Error,
        true
      );
      throw error;
    }
  }

  /**
   * Add a listener for network changes
   */
  onNetworkChange(listener: (chainId: number) => void): () => void {
    this.networkChangeListeners.add(listener);
    
    return () => {
      this.networkChangeListeners.delete(listener);
    };
  }

  /**
   * Get current chain ID (cached)
   */
  getCurrentChainIdCached(): number | null {
    return this.currentChainId;
  }

  /**
   * Check if connected to BSC
   */
  isConnectedToBSC(): boolean {
    return this.currentChainId === BSC_NETWORK_ID;
  }

  /**
   * Check if connected to Ethereum
   */
  isConnectedToEthereum(): boolean {
    return this.currentChainId === DEFAULT_NETWORK_ID;
  }

  /**
   * Get recommended gas settings for current network
   */
  async getRecommendedGasSettings(): Promise<{
    gasPrice?: string;
    maxFeePerGas?: string;
    maxPriorityFeePerGas?: string;
    gasLimit: string;
  }> {
    try {
      const chainId = await this.getCurrentChainId();
      
      // BSC uses legacy gas pricing
      if (chainId === BSC_NETWORK_ID || chainId === 97) {
        const gasPrice = await this.getGasPrice();
        return {
          gasPrice,
          gasLimit: '21000' // Default gas limit
        };
      }
      
      // Ethereum uses EIP-1559 gas pricing
      if (chainId === DEFAULT_NETWORK_ID || chainId === 11155111) {
        try {
          // Try to get EIP-1559 gas fees
          await (window.ethereum as EnhancedEthereumProvider)?.request({ method: 'eth_feeHistory', params: ['0x1', 'latest', []] });
          
          return {
            maxFeePerGas: '0x2540be400', // 10 gwei default
            maxPriorityFeePerGas: '0x77359400', // 2 gwei default
            gasLimit: '21000'
          };
        } catch {
          // Fallback to legacy gas pricing
          const gasPrice = await this.getGasPrice();
          return {
            gasPrice,
            gasLimit: '21000'
          };
        }
      }

      // Default fallback
      const gasPrice = await this.getGasPrice();
      return {
        gasPrice,
        gasLimit: '21000'
      };

    } catch (error) {
      // Return safe defaults
      return {
        gasPrice: '0x2540be400', // 10 gwei
        gasLimit: '21000'
      };
    }
  }

  /**
   * Setup network change event listeners
   */
  private setupNetworkChangeListeners(): void {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    (window.ethereum as EnhancedEthereumProvider).on?.('chainChanged', (chainIdHex: string) => {
      const chainId = parseInt(chainIdHex, 16);
      this.currentChainId = chainId;
      this.notifyNetworkChange(chainId);
      
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.LOW,
        `Network changed to ${getNetworkDisplayName(chainId)} (${chainId})`,
        `Switched to ${getNetworkDisplayName(chainId)}`,
        { chainId, chainIdHex },
        undefined,
        false
      );
    });
  }

  /**
   * Notify all listeners of network change
   */
  private notifyNetworkChange(chainId: number): void {
    this.networkChangeListeners.forEach(listener => {
      try {
        listener(chainId);
      } catch (error) {
        console.error('Error in network change listener:', error);
      }
    });
  }

  /**
   * Clean up event listeners
   */
  cleanup(): void {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    (window.ethereum as EnhancedEthereumProvider).removeListener?.('chainChanged', () => {});
    this.networkChangeListeners.clear();
  }
}

// Singleton instance
export const networkService = new NetworkService();

/**
 * Utility functions for network operations
 */

/**
 * Format chain ID to hex string
 */
export const formatChainIdToHex = (chainId: number): string => {
  return `0x${chainId.toString(16)}`;
};

/**
 * Parse chain ID from hex string
 */
export const parseChainIdFromHex = (chainIdHex: string): number => {
  return parseInt(chainIdHex, 16);
};

/**
 * Get network-specific transaction parameters
 */
export const getNetworkTransactionParams = async (chainId: number) => {
  const networkConfig = getNetworkConfig(chainId);
  if (!networkConfig) {
    throw new Error(`Unsupported network: ${chainId}`);
  }

  try {
    const gasSettings = await networkService.getRecommendedGasSettings();
    
    return {
      chainId: networkConfig.chainIdHex,
      ...gasSettings
    };
  } catch (error) {
    // Return safe defaults
    return {
      chainId: networkConfig.chainIdHex,
      gasPrice: '0x2540be400', // 10 gwei
      gasLimit: '0x5208' // 21000
    };
  }
};

/**
 * Check if wallet supports network addition
 */
export const supportsNetworkAddition = (): boolean => {
  if (typeof window === 'undefined' || !window.ethereum) {
    return false;
  }

  // MetaMask and most modern wallets support wallet_addEthereumChain
  return !!(window.ethereum as any)?.isMetaMask || 
         !!(window.ethereum as any)?.isCoinbaseWallet ||
         typeof (window.ethereum as any)?.request === 'function';
};

/**
 * Get wallet-specific network addition method
 */
export const getWalletNetworkMethod = (): 'metamask' | 'walletconnect' | 'unknown' => {
  if (typeof window === 'undefined' || !window.ethereum) {
    return 'unknown';
  }

  if ((window.ethereum as any)?.isMetaMask) {
    return 'metamask';
  }

  if ((window.ethereum as any)?.isCoinbaseWallet) {
    return 'metamask'; // Coinbase Wallet uses same methods as MetaMask
  }

  return 'walletconnect';
};

/**
 * Get optimal RPC URL for a network (with fallback)
 */
export const getOptimalRpcUrl = async (chainId: number): Promise<string> => {
  const networkConfig = getNetworkConfig(chainId);
  if (!networkConfig) {
    throw new Error(`Unsupported network: ${chainId}`);
  }

  // For now, return the first RPC URL
  // In a production environment, you might want to test RPC URLs for speed/reliability
  return networkConfig.rpcUrls[0];
};

/**
 * Detect if user is on mobile device
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Get deep link for mobile wallet connection
 */
export const getMobileWalletDeepLink = (chainId: number, walletName: string): string | null => {
  if (!isMobileDevice()) return null;
  
  const networkConfig = getNetworkConfig(chainId);
  if (!networkConfig) return null;

  // Common mobile wallet deep link patterns
  switch (walletName.toLowerCase()) {
    case 'metamask':
      return `https://metamask.app.link/dapp/${window.location.host}`;
    case 'trust wallet':
      return `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(window.location.href)}`;
    case 'coinbase wallet':
      return `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`;
    default:
      return null;
  }
};

/**
 * Initialize network service on import
 */
if (typeof window !== 'undefined') {
  networkService.initialize().catch(error => {
    console.warn('Failed to initialize network service:', error);
  });
}